/**
 * Feature Notifications Manager
 * Handles persistent dismissal of feature tutorial notifications
 */

import { USER_WORKER_URL } from '../config.js';
import { storage } from './storage.js';

const HANGOUT_FEATURE_NOTIFICATIONS_ENABLED_STORAGE_KEY = 'hangout_feature_notifications_enabled';

/**
 * Global gate for Hangout "feature promo" notifications.
 * Default is disabled (returns false) unless explicitly enabled in localStorage.
 * @returns {boolean}
 */
export const areHangoutFeatureNotificationsEnabled = () => {
  try {
    const value = localStorage.getItem(HANGOUT_FEATURE_NOTIFICATIONS_ENABLED_STORAGE_KEY);
    // Default OFF if not set
    if (value == null) return false;
    return value === 'true';
  } catch (_) {
    // If localStorage is blocked/unavailable, keep promos disabled.
    return false;
  }
};

/**
 * Check if a feature notification has been dismissed by the user
 * @param {string} notificationKey - The notification key (e.g., 'daily_rewards')
 * @returns {Promise<boolean>} True if dismissed, false otherwise
 */
export async function isFeatureNotificationDismissed(notificationKey) {
  try {
    console.log(`[FeatureNotifications] Checking if "${notificationKey}" is dismissed`);
    const userData = storage.get('userData');
    if (!userData || !userData.isLoggedIn) {
      // For non-logged-in users, check sessionStorage
      const isDismissed = sessionStorage.getItem(`hangout_${notificationKey}_shown`) !== null;
      console.log(`[FeatureNotifications] Not logged in, sessionStorage check: ${isDismissed}`);
      return isDismissed;
    }

    const userId = userData.id;
    if (!userId) {
      const isDismissed = sessionStorage.getItem(`hangout_${notificationKey}_shown`) !== null;
      console.log(`[FeatureNotifications] No user_id, sessionStorage check: ${isDismissed}`);
      return isDismissed;
    }

    console.log(`[FeatureNotifications] Calling USER_WORKER_URL for user ${userId}`);
    
    // Check database for logged-in users
    const response = await fetch(`${USER_WORKER_URL}/check-feature-notification-dismissed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await window.tokenManager?.getValidAccessToken?.() ?? '')}`
      },
      body: JSON.stringify({
        user_id: userId,
        notification_key: notificationKey
      })
    });

    if (!response.ok) {
      console.warn(`[FeatureNotifications] API call failed (${response.status}), falling back to sessionStorage`);
      return sessionStorage.getItem(`hangout_${notificationKey}_shown`) !== null;
    }

    const data = await response.json();
    console.log(`[FeatureNotifications] API response for "${notificationKey}":`, data);
    return data.dismissed || false;
  } catch (error) {
    console.warn('[FeatureNotifications] Error checking dismissal status:', error);
    // Fallback to sessionStorage
    return sessionStorage.getItem(`hangout_${notificationKey}_shown`) !== null;
  }
}

/**
 * Mark a feature notification as dismissed
 * @param {string} notificationKey - The notification key (e.g., 'daily_rewards')
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export async function dismissFeatureNotification(notificationKey) {
  try {
    console.log(`[FeatureNotifications] Dismissing notification "${notificationKey}"`);
    // Always mark in sessionStorage for immediate feedback
    sessionStorage.setItem(`hangout_${notificationKey}_shown`, 'true');

    const userData = storage.get('userData');
    if (!userData || !userData.isLoggedIn) {
      // For non-logged-in users, sessionStorage is enough
      console.log(`[FeatureNotifications] Not logged in, using sessionStorage only`);
      return true;
    }

    const userId = userData.id;
    if (!userId) {
      console.log(`[FeatureNotifications] No user_id, using sessionStorage only`);
      return true;
    }

    console.log(`[FeatureNotifications] Calling USER_WORKER_URL to persist dismissal for user ${userId}`);
    
    // Store in database for logged-in users
    const response = await fetch(`${USER_WORKER_URL}/dismiss-feature-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await window.tokenManager?.getValidAccessToken?.() ?? '')}`
      },
      body: JSON.stringify({
        user_id: userId,
        notification_key: notificationKey
      })
    });

    if (!response.ok) {
      console.warn(`[FeatureNotifications] Failed to store dismissal in database (${response.status})`);
      return false;
    }

    const data = await response.json();
    console.log(`[FeatureNotifications] Dismissal stored successfully:`, data);
    return true;
  } catch (error) {
    console.warn('[FeatureNotifications] Error dismissing notification:', error);
    return false;
  }
}

